'use client';

import React from 'react';
import Link from 'next/link';
import { Eye, Clock, Tag, ArrowRight } from 'lucide-react';
import { useArticles } from '@/context/ArticlesContext';

interface ArticleCardProps {
  id: string;
  title: string;
  excerpt: string;
  tags: string[];
  section: string;
  lastUpdated: string;
  viewCount: number;
  pinned?: boolean;
  href: string;
}

export default function ArticleCard({
  id,
  title,
  excerpt,
  tags,
  section,
  lastUpdated,
  viewCount,
  pinned = false,
  href,
}: ArticleCardProps) {
  const { setSelectedArticleId } = useArticles();

  return (
    <Link href={href} className="block group" onClick={() => setSelectedArticleId(id)}>
      <div className="card-luxury p-5 h-full flex flex-col gap-3">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              {pinned && (
                <span className="pin-badge">Featured</span>
              )}
              <span className="text-[11px] text-muted-foreground font-medium uppercase tracking-wider">{section}</span>
            </div>
            <h3 className="font-semibold text-sm text-foreground group-hover:text-gold transition-colors duration-200 leading-snug line-clamp-2">
              {title}
            </h3>
          </div>
        </div>

        {/* Excerpt */}
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 flex-1">
          {excerpt}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5">
          {tags.slice(0, 3).map(tag => (
            <span key={`article-tag-${title}-${tag}`} className="tag-gold flex items-center gap-1">
              <Tag className="w-2.5 h-2.5" />
              {tag}
            </span>
          ))}
          {tags.length > 3 && (
            <span className="tag-gold">+{tags.length - 3}</span>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-1 border-t border-border">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {lastUpdated}
            </span>
            <span className="flex items-center gap-1 tabular-nums">
              <Eye className="w-3 h-3" />
              {viewCount.toLocaleString()}
            </span>
          </div>
          <ArrowRight className="w-3.5 h-3.5 text-gold opacity-0 group-hover:opacity-100 transition-all duration-200" />
        </div>
      </div>
    </Link>
  );
}